import * as React from "react";
import {useEffect, useState, useMemo, useCallback } from "react";
import {Button, ButtonGroup, Icon, Segment} from "semantic-ui-react";
import {HandsonTable} from "./HandsonTable";

const ExcelJS = require('exceljs');

// Safety cap to prevent browser freeze ex check ticket #4095
const MAX_ROWS = 200;
const MAX_COLS = 50;

export interface XlsxViewer2Props {
    fileBuffer: any;
}

export function XlsxViewer({fileBuffer}: XlsxViewer2Props) {
    const [loading, setLoading] = useState(true);
    const [data, setData] = React.useState([]);
    const [sheetIndex, setSheetIndex] = React.useState(0);
    const [workbook, setWorkbook] = React.useState(null);
    const [mergedCells, setMergedCells] = React.useState([]);
    const [truncated, setTruncated] = React.useState(false);

    useEffect(() => {
        readXlsx();
    }, [fileBuffer]);

    async function readXlsx() {
        setLoading(true);
        let wb = new ExcelJS.Workbook();
        try {
            await wb.xlsx.load(fileBuffer);
        } catch (error) {
            console.error("Error loading XLSX file:", error);
            setLoading(false);
            return;
        }
        setWorkbook(wb);
        openSheet(0, wb);
        setLoading(false);
    }

    function openRow(row: any, highestColumnIndex: number) {
        const newRow = [];
        for (let i = 1; i <= highestColumnIndex; i++) {
            let cell = row[i];
            if (cell) {
                if (cell?.formula) {
                    newRow.push({ formula: "=" + cell.formula });
                } else if (cell?.richText) {
                    const plainText = cell.richText
                        .map(part => part.text)
                        .join('');
                    newRow.push({ value: plainText });
                } else {
                    newRow.push({ value: cell ?? "" });
                }
            } else {
                newRow.push({ value: "" });
            }
        }
        return newRow;
    }

    function openSheet(index: number = sheetIndex, wb = workbook) {
        try {
            if (!wb) return;
            const worksheet = wb.worksheets[index];
            if (!worksheet) return;

            const sheetData = worksheet.getSheetValues();

            let highestColumnIndex = 0;

            for (let i = 0; i < sheetData.length; i++) {
                const row = sheetData[i];
                if (!row) continue;

                const keys = Object.keys(row);
                if (keys.length > 0) {
                    const maxInRow = Math.max(
                        ...keys.map(k => parseInt(k)).filter(n => !isNaN(n))
                    );
                    if (maxInRow > highestColumnIndex) {
                        highestColumnIndex = maxInRow;
                    }
                }
            }

            highestColumnIndex = Math.min(highestColumnIndex, MAX_COLS);
            const maxRows = Math.min(sheetData.length, MAX_ROWS);
            const wasTruncated =
                sheetData.length > MAX_ROWS || highestColumnIndex > MAX_COLS;
            setTruncated(wasTruncated);

            const fixedSheetData = [];
            const emptyRow = () => {
                const row = [];
                for (let j = 1; j <= highestColumnIndex; j++) {
                    row.push({ value: "" });
                }
                return row;
            };

            for (let i = 1; i <= maxRows; i++) {
                let row = sheetData[i];
                if (row) {
                    fixedSheetData.push(openRow(row, highestColumnIndex));
                } else {
                    fixedSheetData.push(emptyRow());
                }
            }

            setData(fixedSheetData);
            calculateMergedCells(worksheet);
        }catch(e) {
            console.error("Failed to open sheet!", e);
        }
    }

    function calculateMergedCells(worksheet) {
        const merges = worksheet._merges;
        const mergedCells = Object.values(merges).map((merge: any) => {
            let model = merge.model;
            return {
                row: model.top - 1,
                col: model.left - 1,
                rowspan: (model.bottom - model.top) + 1,
                colspan: (model.right - model.left) + 1,
            };
        });
        setMergedCells(mergedCells);
    }

    const worksheetButtons = useMemo(() => {
        if (!workbook) return null;

        return (
            <div style={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'flex-start',
                alignItems: 'center',
                overflowX: 'auto',
                overflowY: 'hidden',
                marginTop: '0.5em',
                flexShrink: 0,
                flexGrow: 0,
                gap: '8px',
                paddingBottom: '4px',
            }}>
                <ButtonGroup>
                    {workbook.worksheets.map((sheet, index) => (
                        <Button
                            key={index}
                            primary={index === sheetIndex}
                            onClick={() => {
                                setSheetIndex(index);
                                openSheet(index);
                            }}
                        >
                            {sheet.name}
                        </Button>
                    ))}
                </ButtonGroup>
                <Icon name={"info circle"} color={"blue"} size={"large"} />
                <span style={{whiteSpace: 'nowrap'}}>
                Note: This is a preview. Download the file for full functionality.
            </span>
            </div>
        );
    }, [workbook, sheetIndex]);

    if (loading) {
        return <div>Loading...</div>;
    }

    return (
        <div style={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            flex: 1,
            minHeight: 0,
            overflow: 'hidden',
        }}>
            <Segment style={{
                display: 'flex',
                flexDirection: 'column',
                flex: 1,
                minHeight: 0,
                overflow: 'auto',
                margin: 0,
            }}>
                {truncated && (
                    <div style={{
                        background: '#fff3cd',
                        padding: '8px',
                        borderRadius: '4px',
                        marginBottom: '8px'
                    }}>
                        ⚠️ Preview truncated to {MAX_ROWS} rows / {MAX_COLS} columns to avoid CM freeze. Download the file for full data.
                    </div>
                )}
                <HandsonTable data={data} mergedCells={mergedCells} />
            </Segment>
            {worksheetButtons}
        </div>
    );
}